import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

/**
 * Trailing pointer ring.
 *
 * Only mounts for devices that actually have a fine pointer, and follows on a
 * quickTo so it lags the real cursor slightly. Elements opt into the larger
 * state with data-cursor, and data-cursor-label swaps in a word such as View.
 */
export const Cursor: FC = (): ReactNode => {
    const dot = useRef<HTMLDivElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = dot.current

        if (!element) {
            return
        }

        const fine = window.matchMedia('(pointer: fine)').matches
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (!fine || reduced) {
            return
        }

        gsap.set(element, { xPercent: -50, yPercent: -50, opacity: 0 })

        const moveX = gsap.quickTo(element, 'x', { duration: 0.42, ease: 'power3.out' })
        const moveY = gsap.quickTo(element, 'y', { duration: 0.42, ease: 'power3.out' })

        const label = element.querySelector<HTMLElement>('[data-cursor-text]')

        const onMove = (event: PointerEvent) => {
            moveX(event.clientX)
            moveY(event.clientY)

            gsap.to(element, { opacity: 1, duration: 0.3, overwrite: 'auto' })

            const target = event.target as HTMLElement | null
            const hot = target?.closest<HTMLElement>('[data-cursor]')
            const interactive = target?.closest('a, button, input, textarea, select')

            if (hot) {
                const text = hot.getAttribute('data-cursor-label') ?? ''

                if (label) {
                    label.textContent = text
                }

                gsap.to(element, {
                    width: 84,
                    height: 84,
                    backgroundColor: 'oklch(0.955 0.005 85 / 0.92)',
                    borderColor: 'transparent',
                    duration: 0.35,
                    ease: 'power3.out',
                    overwrite: 'auto'
                })

                return
            }

            if (label) {
                label.textContent = ''
            }

            gsap.to(element, {
                width: interactive ? 46 : 22,
                height: interactive ? 46 : 22,
                backgroundColor: 'transparent',
                borderColor: 'oklch(0.955 0.005 85 / 0.55)',
                duration: 0.35,
                ease: 'power3.out',
                overwrite: 'auto'
            })
        }

        const onLeave = () => gsap.to(element, { opacity: 0, duration: 0.25 })

        window.addEventListener('pointermove', onMove)
        document.addEventListener('pointerleave', onLeave)

        return () => {
            window.removeEventListener('pointermove', onMove)
            document.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    return (
        <div
            ref={dot}
            aria-hidden='true'
            className='pointer-events-none fixed left-0 top-0 z-[80] hidden h-[22px] w-[22px] items-center justify-center rounded-full border border-foreground/55 opacity-0 mix-blend-difference md:flex'
        >
            <span
                data-cursor-text
                className='label text-[9px] text-background mix-blend-normal'
            />
        </div>
    )
}
